import React, { useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { ArrowRightIcon, BriefcaseIcon, InfoIcon, MapPinIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useEscaCopy } from '../../i18n/escaCopy';
import { track } from '../../utils/analytics';

/**
 * PLACEHOLDER CONTENT — the three cases come from i18n/escaCopy.ts and are
 * illustrative constructions. Replace with verified stories before launch.
 */
export function ClassicCases() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const toggle = (i: number, role: string) => {
    const next = openIdx === i ? null : i;
    setOpenIdx(next);
    if (next !== null) track('case_open', {
      case_role: role,
      variant: 'classic'
    });
  };
  return <section id="cases" className="relative py-20 lg:py-28 bg-white overflow-hidden" data-id="element-535" data-mp="3127wex6jzk1b">
      <div className="absolute inset-0 pointer-events-none" data-id="element-536" data-mp="6g9yrtp1c0z0">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-cyan-50/30 blur-3xl hidden sm:block" data-id="element-537" data-mp="6fqj6jmuxoo5" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-538" data-mp="3tkzls15ryhek">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-6" data-id="element-539" data-mp="3ew3dq43px4dy">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-540" data-mp="35vnc706jc7bq">
            Cases
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight max-w-4xl mx-auto" data-id="element-541" data-mp="3avjjb8aj4evi">
            {c.cases.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto leading-relaxed" data-id="element-542" data-mp="30w0zcrumct3g">
            {c.cases.lead}
          </p>
        </motion.div>

        <div className="flex items-start gap-2.5 justify-center max-w-3xl mx-auto mb-12 lg:mb-14" data-id="element-543" data-mp="2ht8rbs0mk653">
          <InfoIcon className="flex-shrink-0 w-4 h-4 mt-0.5 text-navy-400" data-id="element-544" data-mp="2cze4wgtmxd2b" />
          <p className="text-sm text-navy-400 leading-relaxed" data-id="element-545" data-mp="282xj3t9wrase">
            {c.cases.placeholderNote}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6" data-id="element-546" data-mp="234m7bd01yq7j">
          {c.cases.items.map((item, i) => {
          const isOpen = openIdx === i;
          return <motion.article key={item.role + item.industry} initial={{
            opacity: 0,
            y: 24
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.1 + i * 0.08
          }} className="flex flex-col rounded-2xl bg-white border border-navy-200 shadow-sm hover:shadow-xl hover:shadow-navy-200/40 transition-shadow duration-200 overflow-hidden" data-id="element-547" data-mp="2vsi3mglpmg3s" data-mpi={i}>
                <div className="p-6 lg:p-7 flex-1 flex flex-col" data-id="element-548" data-mp="3m1h8kz5sz4lt">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-primary-100 to-primary-50 text-primary-600" data-id="element-549" data-mp="3m3afmfkbvquc">
                    <BriefcaseIcon className="w-5 h-5" data-id="element-550" data-mp="3vzde9bfz17cj" />
                  </div>

                  <h3 className="mt-5 text-2xl font-extrabold text-navy-900 tracking-tight" data-id="element-551" data-mp="4kpn9zt7lhl4">
                    {item.role}
                  </h3>

                  <div className="mt-3 flex flex-wrap gap-2" data-id="element-552" data-mp="9jj2bwo59gne">
                    <span className="px-2.5 py-1 rounded-md bg-navy-50 text-xs font-medium text-navy-600" data-id="element-553" data-mp="dkw703fi3dsq">
                      {item.industry}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-navy-50 text-xs font-medium text-navy-600" data-id="element-554" data-mp="njtpaomc2b3a">
                      <MapPinIcon className="w-3 h-3" data-id="element-555" data-mp="2kd5b4sqvkjnv" />
                      {item.geo}
                    </span>
                  </div>

                  <div className="mt-6 pt-6 border-t border-navy-100" data-id="element-556" data-mp="2pdaxbtedldyk">
                    <p className="text-xs font-semibold uppercase tracking-wide text-navy-400" data-id="element-557" data-mp="jlms4hp000wq">
                      {c.cases.challengeLabel}
                    </p>
                    <p className="mt-2 text-sm text-navy-600 leading-relaxed" data-id="element-558" data-mp="so4bmod9qfg1">
                      {item.challenge}
                    </p>
                  </div>

                  <AnimatePresence initial={false} data-id="element-559" data-mp="ok6cweruv6ni">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-560" data-mp="uc1m1n6pq5d">
                        <div className="pt-5" data-id="element-561" data-mp="3dd1u3ocjvzhe">
                          <p className="text-xs font-semibold uppercase tracking-wide text-navy-400" data-id="element-562" data-mp="3dbqpjmba6np8">
                            {c.cases.approachLabel}
                          </p>
                          <p className="mt-2 text-sm text-navy-600 leading-relaxed" data-id="element-563" data-mp="2z6qh82o6ar5m">
                            {item.approach}
                          </p>
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  <div className="mt-auto pt-6" data-id="element-564" data-mp="2ua9qr1ti40pd">
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary-600" data-id="element-565" data-mp="2u6vm1mxd7fer">
                      {c.cases.resultLabel}
                    </p>
                    <p className="mt-2 text-base font-bold text-navy-900 leading-snug" data-id="element-566" data-mp="3mxdpwq5wxkt8">
                      {item.result}
                    </p>
                  </div>
                </div>

                <button type="button" onClick={() => toggle(i, item.role)} aria-expanded={isOpen} className="group flex items-center justify-between gap-3 w-full px-6 lg:px-7 py-4 border-t border-navy-100 text-left text-sm font-semibold text-primary-700 hover:bg-primary-50/60 transition-colors duration-200" data-id="element-567" data-mp="3cyetc5yf9fml">
                  {c.cases.cta}
                  <ArrowRightIcon className={`w-4 h-4 transition-transform duration-200 ease-out ${isOpen ? 'rotate-90' : 'group-hover:translate-x-1'}`} data-id="element-568" data-mp="353eubslh1kp" />
                </button>
              </motion.article>;
        })}
        </div>
      </div>
    </section>;
}